// src/navigation/NavigationList.jsx
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useNavigationGroups, classNames } from "./menuItems";

export default function NavigationList({ setSidebarOpen }) {
  const { t } = useTranslation();
  const { navigationGroups, loadingNav, navError } = useNavigationGroups();
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (groupName) => {
    setExpandedGroups((groups) => ({
      ...groups,
      [groupName]: !groups[groupName],
    }));
  };

  return (
    <ul className="flex flex-1 flex-col gap-y-7">
      {loadingNav && (
        <li className="px-2 text-xs text-indigo-200">{t("sidebar.status.loadingCollections")}</li>
      )}
      {navError && (
        <li className="px-2 text-xs text-rose-300">
          {t("sidebar.status.failedCollections")}
        </li>
      )}

      {navigationGroups.map((group) => (
        <li key={group.name}>
          {group.collapsible ? (
            <button
              type="button"
              onClick={() => toggleGroup(group.name)}
              aria-expanded={!!expandedGroups[group.name]}
              className="mb-2 flex w-full items-center justify-between text-xs/6 font-semibold text-indigo-100 hover:text-white"
            >
              {group.name}
              <ChevronDownIcon
                aria-hidden="true"
                className={classNames(
                  expandedGroups[group.name] && "rotate-180",
                  "size-4 transition-transform"
                )}
              />
            </button>
          ) : (
            <div className="text-xs/6 font-semibold text-indigo-100 mb-2">
              {group.name}
            </div>
          )}

          <ul
            className={classNames(
              group.collapsible && !expandedGroups[group.name] && "hidden",
              "-mx-2 space-y-1"
            )}
          >
            {group.items.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  onClick={() => setSidebarOpen && setSidebarOpen(false)}
                  className={({ isActive }) =>
                    classNames(
                      isActive
                        ? "bg-indigo-950/25 text-white"
                        : "text-indigo-100 hover:bg-indigo-950/25 hover:text-white",
                      "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold capitalize"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.icon && (
                        <item.icon
                          aria-hidden="true"
                          className={classNames(
                            isActive
                              ? "text-white"
                              : "text-indigo-100 group-hover:text-white",
                            "size-6 shrink-0"
                          )}
                        />
                      )}
                      <span className="min-w-0 flex-1">{item.name}</span>
                      {item.badge > 0 ? (
                        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-xs font-semibold text-white">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}
